import type { ReactNode } from "react";

import { ContentCard } from "@narmi/design_system";
import { useDashboardLayoutRedesignEnabled } from "byzantine";
import { useMarketingTarget } from "byzantine/src/dbbl/businessLogic/modules/marketing/marketing.container";
import cc from "classcat";

import SidebarPromotionCard from "../SidebarPromotionCard/SidebarPromotionCard";
import { useExistingUserSidebarGuidesContainer } from "../SidebarGuide/containers";
import useIsMarketingBannersEnabled from "../../../../components/dashboard/MarketingImage/hooks";
import useWelcomeOffer from "../../../../components/dashboard/MarketingCardContainer/useWelcomeOffer";

import styles from "./SidebarMarketingCard.module.scss";
import SidebarMarketingCard from "./SidebarMarketingCard";

import type { MarketingTarget } from "../../../../components/dashboard/MarketingCard/types";
import type { PromotionCardProps } from "../../../../components/dashboard/PromotionCard/types";

const ADDRESS = "31b65103-a70b-4a35-8245-124a91fe1fe0";

type CardWrapperProps = {
  isCollapsed: boolean;
  children: ReactNode;
};

const CardWrapper = ({ isCollapsed, children }: CardWrapperProps) => {
  const { isLoading, stackedPrompts } = useExistingUserSidebarGuidesContainer();

  if (isLoading) {
    return null;
  }

  const containerClasses = cc([
    styles.container,
    isCollapsed ? "" : styles["container--expanded"],
    stackedPrompts.length === 0 ? "" : styles["container--hidden"],
  ]);

  return (
    <div className={containerClasses}>
      <ContentCard kind="bordered" paddingSize="m">
        {children}
      </ContentCard>
    </div>
  );
};

const MarketingCard = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const { loading, config, linkUrl, opensInNewTab } =
    useMarketingTarget<MarketingTarget>(ADDRESS);

  if (loading || !config || !linkUrl) {
    return null;
  }

  return (
    <CardWrapper isCollapsed={isCollapsed}>
      <SidebarMarketingCard
        address={ADDRESS}
        config={config}
        linkUrl={linkUrl}
        opensInNewTab={opensInNewTab}
      />
    </CardWrapper>
  );
};

const PromotionCard = ({
  isCollapsed,
  promotion,
}: PromotionCardProps & { isCollapsed: boolean }) => (
  <CardWrapper isCollapsed={isCollapsed}>
    <SidebarPromotionCard promotion={promotion} />
  </CardWrapper>
);

const SidebarMarketingCardContainer = ({
  isCollapsed,
}: {
  isCollapsed: boolean;
}) => {
  const welcomeOffer = useWelcomeOffer();
  const isMarketingBannersEnabled = useIsMarketingBannersEnabled();
  const isDashboardLayoutRedesignEnabled = useDashboardLayoutRedesignEnabled();

  if (!isDashboardLayoutRedesignEnabled) {
    return null;
  }

  if (welcomeOffer && !isMarketingBannersEnabled) {
    return <PromotionCard isCollapsed={isCollapsed} promotion={welcomeOffer} />;
  }

  if (isMarketingBannersEnabled) {
    return <MarketingCard isCollapsed={isCollapsed} />;
  }

  return null;
};

export default SidebarMarketingCardContainer;
